import type { MatchPhase } from '../schemas/matches.js'

export type TeamCupEventLike = { targetTeamId: string; cupIndex: number; isRedemption: boolean; isRedeemed: boolean; createdAt: number }
export type RearrangementLike = { targetTeamId: string; moves: readonly { from: number; to: number }[]; createdAt: number }

// `| string` resta perché la colonna `phase` letta dal DB (backend) è tipizzata `string`, non `MatchPhase`.
export function getCupsForPhase(phase: MatchPhase | string, settings: { cupsPerTeamGroup: number; cupsPerTeamFinal: number }): number {
  return phase === 'group' ? settings.cupsPerTeamGroup : settings.cupsPerTeamFinal
}

/**
 * Posizioni effettivamente prese di una squadra: gli eventi prima di uno spostamento usano le posizioni
 * vecchie, lo spostamento rimappa (`from` diventa preso, `to` torna libero), gli eventi dopo usano le nuove.
 */
export function computeEffectiveTaken(events: readonly TeamCupEventLike[], rearrangements: readonly RearrangementLike[], teamId: string): Set<number> {
  const teamEvents = events.filter((e) => e.targetTeamId === teamId && !e.isRedemption && !e.isRedeemed)
  const teamMoves = rearrangements.filter((r) => r.targetTeamId === teamId).sort((a, b) => a.createdAt - b.createdAt)
  const taken = new Set<number>()
  if (teamMoves.length === 0) {
    for (const e of teamEvents) taken.add(e.cupIndex)
    return taken
  }
  for (const r of teamMoves) {
    for (const e of teamEvents) if (e.createdAt < r.createdAt) taken.add(e.cupIndex)
    for (const move of r.moves) {
      taken.add(move.from)
      taken.delete(move.to)
    }
  }
  const last = teamMoves[teamMoves.length - 1].createdAt
  for (const e of teamEvents) if (e.createdAt >= last) taken.add(e.cupIndex)
  return taken
}

/** Fase del pareggio: la squadra con tutti i bicchieri presi (se c'è) può tirare il tiro di redenzione. */
export function redemptionState(
  events: readonly TeamCupEventLike[],
  rearrangements: readonly RearrangementLike[],
  homeTeamId: string,
  awayTeamId: string,
  maxCups: number,
): { losingTeamId: string | null } {
  if (computeEffectiveTaken(events, rearrangements, homeTeamId).size >= maxCups) return { losingTeamId: homeTeamId }
  if (computeEffectiveTaken(events, rearrangements, awayTeamId).size >= maxCups) return { losingTeamId: awayTeamId }
  return { losingTeamId: null }
}
